import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { BRANCH_LABELS, type BranchCode } from '@lorsamthai/ops-shared';
import {
  createTransferDayTrs,
  listTransferDay,
  runNightly,
  type TransferDayRow,
} from '../lib/queries.js';
import { Badge } from '../ui/Badge.js';
import { fmtInt } from '../lib/format.js';
import { useAuth } from '../auth/context.js';
import { resolveSkuDisplay } from '../lib/skuParser.js';
import { formatCopyText } from '../lib/copyFormat.js';

interface RowState {
  checked: boolean;
  qty: number;
}

export default function TransferDay() {
  const { me } = useAuth();
  const [rows, setRows] = useState<TransferDayRow[]>([]);
  const [state, setState] = useState<Record<string, RowState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [done, setDone] = useState<{ count: number; refs: string[] } | null>(null);

  const rowKey = (r: { sku: string; dest_branch: BranchCode }) => `${r.dest_branch}::${r.sku}`;

  const load = async () => {
    setLoading(true);
    setError(null);
    setDone(null);
    try {
      const r = await listTransferDay();
      setRows(r.items);
      const init: Record<string, RowState> = {};
      for (const row of r.items) {
        init[rowKey(row)] = {
          checked: row.suggested_qty > 0,
          qty: row.suggested_qty,
        };
      }
      setState(init);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const refresh = async () => {
    if (me?.role !== 'super_admin') {
      await load();
      return;
    }
    setRefreshing(true);
    setError(null);
    try {
      await runNightly();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  // Group by destination branch
  const groups = useMemo(() => {
    const map = new Map<BranchCode, TransferDayRow[]>();
    for (const r of rows) {
      const list = map.get(r.dest_branch) ?? [];
      list.push(r);
      map.set(r.dest_branch, list);
    }
    return Array.from(map.entries()).map(([branch, items]) => ({ branch, items }));
  }, [rows]);

  const totalPicked = useMemo(() => {
    let n = 0;
    for (const r of rows) if (state[rowKey(r)]?.checked) n += 1;
    return n;
  }, [rows, state]);

  const totalQty = useMemo(() => {
    let q = 0;
    for (const r of rows) if (state[rowKey(r)]?.checked) q += state[rowKey(r)]!.qty;
    return q;
  }, [rows, state]);

  const setRow = (key: string, patch: Partial<RowState>) =>
    setState((p) => ({ ...p, [key]: { ...p[key]!, ...patch } }));

  const toggleGroup = (branch: BranchCode, checked: boolean) => {
    const patch: Record<string, RowState> = { ...state };
    for (const r of rows) {
      if (r.dest_branch !== branch) continue;
      const k = rowKey(r);
      patch[k] = { ...patch[k]!, checked };
    }
    setState(patch);
  };

  const copyGroup = (branch: BranchCode) => {
    const text = formatCopyText(
      rows
        .filter((r) => r.dest_branch === branch && state[rowKey(r)]?.checked)
        .map((r) => ({
          sku: r.sku,
          brand: r.brand,
          model: r.model,
          size: r.size,
          qty: state[rowKey(r)]!.qty,
        }))
    );
    if (!text) return;
    void navigator.clipboard.writeText(text);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const items = rows
        .filter((r) => state[rowKey(r)]?.checked)
        .map((r) => ({
          sku: r.sku,
          qty: state[rowKey(r)]!.qty,
          dest_branch: r.dest_branch,
        }));
      if (items.length === 0) {
        setError('ยังไม่ได้เลือกรายการที่จะโอน');
        setSubmitting(false);
        return;
      }
      const result = await createTransferDayTrs(items);
      setDone({
        count: result.created.length,
        refs: result.created.map((c) => c.tr_ref ?? `#${c.tr_id}`),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500">กำลังโหลด...</div>;

  const today = new Intl.DateTimeFormat('th-TH', { dateStyle: 'full' }).format(new Date());

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">โอนยางวันนี้</h1>
          <p className="mt-1 text-sm text-slate-500">{today} · จากบางพลี → สาขาลูก</p>
          <p className="mt-0.5 text-xs text-slate-400">
            นับเฉพาะยาง DOT ปีปัจจุบัน — ของ DOT ปีเก่าไม่ถูกนับว่าครบ min/max
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing || loading}
          title="รัน run_nightly แล้วโหลดข้อมูลใหม่"
          className="shrink-0 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
        >
          {refreshing ? '⏳' : '🔄'} รีเฟรช
        </button>
      </div>

      {done && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
          ✅ สร้างใบโอน {done.count} ใบแล้ว: {done.refs.join(', ')}
          <Link to="/transfers" className="ml-2 underline">
            ไปดูใน Transfer list
          </Link>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <div className="text-4xl">🎉</div>
          <p className="mt-2 text-sm text-slate-600">
            ไม่มีอะไรต้องโอนวันนี้ — ยาง DOT ปีนี้ของสาขาลูกทุกแห่งเกิน min หรือ
            บางพลีไม่มียาง DOT ปีนี้เหลือพอจะโอน
          </p>
          <p className="mt-1 text-xs text-slate-400">
            (ถ้า bp อยู่ที่ min พอดี ระบบจะสั่ง supplier แทน — ดูที่ Order Day)
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm">
            <span className="font-semibold">{rows.length}</span> SKU ต้องโอน
            {' · '}
            <span className="font-semibold">{groups.length}</span> สาขาปลายทาง
            {' · '}
            <span className="font-semibold">{totalPicked}</span> เลือกแล้ว
            (รวม <span className="font-semibold">{fmtInt(totalQty)}</span> เส้น)
          </div>

          {groups.map(({ branch, items }) => {
            const allChecked = items.every((r) => state[rowKey(r)]?.checked);
            const someChecked = items.some((r) => state[rowKey(r)]?.checked);
            const branchTotal = items.reduce((s, r) => {
              const st = state[rowKey(r)];
              return s + (st?.checked ? st.qty : 0);
            }, 0);
            return (
              <section key={branch} className="rounded-xl border border-slate-200 bg-white">
                <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = !allChecked && someChecked;
                      }}
                      onChange={(e) => toggleGroup(branch, e.target.checked)}
                    />
                    <div>
                      <h2 className="font-semibold">→ {BRANCH_LABELS[branch]}</h2>
                      <div className="text-xs text-slate-500">
                        {items.length} SKU
                        {branchTotal > 0 && ` · เลือกรวม ${fmtInt(branchTotal)} เส้น`}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => copyGroup(branch)}
                    className="rounded-md border border-slate-200 px-3 py-1 text-xs hover:bg-slate-50"
                  >
                    📋 คัดลอกข้อความ
                  </button>
                </header>
                <ul className="divide-y divide-slate-100">
                  {items.map((r) => {
                    const k = rowKey(r);
                    const s = state[k] ?? { checked: false, qty: r.suggested_qty };
                    const dr = resolveSkuDisplay(r.sku, r.brand, r.model, r.size);
        const display = [dr.brand, dr.model, dr.size].filter(Boolean).join(' ');
                    const cappedByBp = r.suggested_qty < r.desired_qty;
                    return (
                      <li key={k} className="flex flex-wrap items-center gap-3 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={s.checked}
                          onChange={(e) => setRow(k, { checked: e.target.checked })}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">
                            {display || <span className="font-mono text-xs">{r.sku}</span>}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-500">
                            <Link to={`/stock/${encodeURIComponent(r.sku)}`} className="font-mono hover:underline">
                              {r.sku}
                            </Link>
                            {' · '}
                            ที่สาขาเหลือ (DOT ปีนี้){' '}
                            <span className="font-semibold">{r.effective_cy}</span> /
                            min <span className="text-slate-600">{r.min_qty}</span>
                            {' · '}
                            bp มี (DOT ปีนี้){' '}
                            <span className="font-semibold">{r.bp_effective_cy}</span>
                            {' '}(โอนได้ {r.bp_avail})
                            {r.velocity > 0 && (
                              <> · ขาย {r.velocity.toFixed(1)}/วัน</>
                            )}
                            {r.days_of_cover !== null && r.days_of_cover < 7 && (
                              <> · <Badge tone={r.days_of_cover < 3 ? 'danger' : 'warn'}>
                                เหลือ {r.days_of_cover.toFixed(0)} วัน
                              </Badge></>
                            )}
                            {cappedByBp && (
                              <> · <Badge tone="warn">bp พอแค่ {r.suggested_qty}/ต้องการ {r.desired_qty}</Badge></>
                            )}
                          </div>
                          {r.on_hand_old > 0 && (
                            <div className="mt-0.5 text-xs text-amber-700">
                              📦 สาขานี้มี DOT ปีเก่าค้าง {r.on_hand_old} เส้น — ไม่นับเป็นของที่ครบ
                              min (รวมทุกปี {r.effective})
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min={1}
                            max={r.bp_avail}
                            value={s.qty}
                            onChange={(e) => setRow(k, { qty: Number(e.target.value) })}
                            className="w-20 rounded-md border border-slate-200 px-2 py-1 text-right text-sm"
                          />
                          <span className="text-xs text-slate-500">เส้น</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}

          {me?.role === 'super_admin' && (
            <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/90 px-4 py-3 backdrop-blur sm:rounded-xl sm:border sm:bg-white">
              <button
                onClick={submit}
                disabled={submitting || totalPicked === 0}
                className="w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {submitting
                  ? 'กำลังบันทึก...'
                  : `บันทึกเป็นใบโอน (${totalPicked} SKU · ${fmtInt(totalQty)} เส้น)`}
              </button>
              <p className="mt-1 text-center text-xs text-slate-500">
                ระบบจะสร้างใบโอน 1 ใบต่อสาขาปลายทาง — สถานะ approved รอ ship
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
